import Link from "next/link";
import React from "react";

/**
 * CTA (Campaign Summary) – minimalist redesign
 * Props: data, targetAmount, amountRaised
 */
const Cta = ({ data, targetAmount, amountRaised }) => {
  // Helpers --------------------------------------------------------------
  const hasMeaningfulFundraisingData = () => {
    const hasValidRaised = typeof amountRaised === "number" && amountRaised > 0;
    const hasValidTarget = typeof targetAmount === "number" && targetAmount > 0;
    return hasValidRaised || hasValidTarget;
  };

  const progressRaw =
    targetAmount > 0 ? (amountRaised / targetAmount) * 100 : 0;
  const progressPct = Math.min(progressRaw, 100);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);

  const campaign = data?.success || {};
  const organization = data?.organization || {};
  const username = data?.username || "Unknown";

  const getUserType = () =>
    organization?.type
      ? organization.type.charAt(0).toUpperCase() +
        organization.type.slice(1).toLowerCase()
      : "Individual";
  const getCategory = () =>
    organization?.category || campaign?.impact?.category || "General";
  const getLocation = () =>
    campaign?.overview?.country || organization?.country || "Global";
  const getRepresentative = () => organization?.name || username;

  // UI ------------------------------------------------------------------
  return (
    <section
      className="rounded-2xl border border-jacarta-200/60 dark:border-jacarta-600 bg-white dark:bg-jacarta-800 shadow-sm transition-colors"
      aria-labelledby="campaign-summary-heading"
    >
      {/* Progress / Hero */}
      {hasMeaningfulFundraisingData() && (
        <div className="p-5 sm:p-6 border-b border-jacarta-200/60 dark:border-jacarta-600">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h2
                id="campaign-summary-heading"
                className="text-sm tracking-wide font-medium text-jacarta-500 dark:text-jacarta-400 uppercase"
              >
                Amount Raised
              </h2>
              <p className="mt-1 text-3xl sm:text-4xl font-semibold text-jacarta-800 dark:text-white tabular-nums">
                {formatCurrency(amountRaised)}
              </p>
              <p className="mt-1 text-xs text-jacarta-500 dark:text-jacarta-400">
                of {formatCurrency(targetAmount)} goal
              </p>
            </div>
            {/* Progress bar */}
            <div className="w-full sm:max-w-xs">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-jacarta-500 dark:text-jacarta-400">
                  Progress
                </span>
                <span className="text-xs font-semibold text-accent">
                  {progressRaw.toFixed(1)}%
                </span>
              </div>
              <div className="h-2 w-full rounded-full bg-jacarta-100 dark:bg-jacarta-700 overflow-hidden">
                <span
                  className="block h-full bg-accent dark:bg-accent-dark transition-[width] duration-700 ease-out"
                  style={{ width: `${progressPct}%` }}
                  aria-label="funding progress"
                  role="progressbar"
                  aria-valuenow={Number(progressRaw.toFixed(1))}
                  aria-valuemin={0}
                  aria-valuemax={100}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Details */}
      <div className="p-5 sm:p-6">
        {!hasMeaningfulFundraisingData() && (
          <h2 id="campaign-summary-heading" className="sr-only">
            Campaign Summary
          </h2>
        )}
        {!hasMeaningfulFundraisingData() && (
          <div className="text-center mb-6">
            <p className="text-xs tracking-wide font-medium text-jacarta-500 dark:text-jacarta-400 uppercase">
              Total Amount Raised
            </p>
            <p className="mt-2 text-3xl font-semibold text-jacarta-800 dark:text-white tabular-nums">
              {formatCurrency(amountRaised)}
            </p>
          </div>
        )}

        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 mb-6">
          <div className="space-y-1">
            <dt className="text-[10px] sm:text-xs tracking-wide font-medium text-jacarta-500 dark:text-jacarta-400 uppercase">
              User Type
            </dt>
            <dd className="text-sm font-semibold text-jacarta-800 dark:text-white break-words">
              {getUserType()}
            </dd>
          </div>
          <div className="space-y-1">
            <dt className="text-[10px] sm:text-xs tracking-wide font-medium text-jacarta-500 dark:text-jacarta-400 uppercase">
              Category
            </dt>
            <dd className="text-sm font-semibold text-jacarta-800 dark:text-white break-words">
              {getCategory()}
            </dd>
          </div>
          <div className="space-y-1">
            <dt className="text-[10px] sm:text-xs tracking-wide font-medium text-jacarta-500 dark:text-jacarta-400 uppercase">
              Location
            </dt>
            <dd className="text-sm font-semibold text-jacarta-800 dark:text-white break-words">
              {getLocation()}
            </dd>
          </div>
          <div className="space-y-1">
            <dt className="text-[10px] sm:text-xs tracking-wide font-medium text-jacarta-500 dark:text-jacarta-400 uppercase">
              Representative
            </dt>
            <dd
              className="text-sm font-semibold text-jacarta-800 dark:text-white truncate"
              title={getRepresentative()}
            >
              {getRepresentative()}
            </dd>
          </div>
        </dl>

        {/* Action */}
        <div className="flex flex-col items-center">
          <Link
            href="/dashboard/create-dot"
            className="inline-flex items-center justify-center gap-2 rounded-md bg-accent hover:bg-accent-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 text-white text-sm font-semibold px-6 py-3 transition-colors w-full sm:w-auto"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v12M6 12h12"
              />
            </svg>
            Create Impact Token
          </Link>
          <p className="mt-3 text-xs text-center text-jacarta-500 dark:text-jacarta-400 max-w-sm">
            Support this campaign and earn impact tokens
          </p>
        </div>
      </div>
    </section>
  );
};

export default Cta;
